import React, { useEffect, useState } from 'react';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import EmptyState from '../components/ui/EmptyState';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import platformApi, { platformErrorMessage } from '../services/platformApi';
import { openInvoicePdf } from './Invoices';
import { canRefund, formatInr, parseRefundInput, refundableRemaining } from '../utils/billingAdmin';

function formatAmount(amount, currency = 'INR') {
  if (amount == null) return '—';
  const major = Number(amount) / 100;
  return `${major.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function RefundForm({ payment, onDone }) {
  const toast = useToast();
  const confirm = useConfirm();
  const remaining = refundableRemaining(payment);
  const [form, setForm] = useState({ rupees: '', reason: '' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const parsed = parseRefundInput(form, remaining);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setError(null);
    const amountText = formatInr(parsed.amount ?? remaining);
    const ok = await confirm({
      title: 'Refund payment?',
      message: `Refund ${amountText} to the customer through ${payment.provider}. ${parsed.amount ? 'This is a partial refund; the plan stays active.' : 'This is a full refund; the workspace moves to the Free plan.'} A credit note is issued if the payment had an invoice.`,
      confirmLabel: `Refund ${amountText}`,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await platformApi.post(`/payments/${payment.id}/refund`, {
        reason: form.reason.trim(),
        ...(parsed.amount ? { amount: parsed.amount } : {}),
      });
      toast.success(`Refund of ${amountText} submitted`);
      setForm({ rupees: '', reason: '' });
      onDone();
    } catch (err) {
      toast.error(platformErrorMessage(err, 'Refund failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card card__body" style={{ marginTop: '1rem' }}>
      <h3 style={{ marginTop: 0 }}>Refund</h3>
      <p className="form-hint">Up to {formatInr(remaining)} can be refunded. Leave the amount empty to refund all of it.</p>
      <div className="form-group">
        <label className="form-label" htmlFor="refund-amount">Amount in ₹ (optional)</label>
        <input id="refund-amount" className="form-input" inputMode="decimal" placeholder={(remaining / 100).toFixed(2)} value={form.rupees} onChange={(e) => setForm((p) => ({ ...p, rupees: e.target.value }))} />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="refund-reason">Reason</label>
        <textarea id="refund-reason" className="form-textarea" rows={2} maxLength={500} value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} />
      </div>
      {error && <div className="alert alert--error">{error}</div>}
      <div className="btn-group" style={{ justifyContent: 'flex-end' }}>
        <button type="submit" className="btn btn--danger-solid" disabled={busy}>{busy ? 'Refunding…' : 'Refund'}</button>
      </div>
    </form>
  );
}

function Payments() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({ status: '', provider: '', organizationId: '' });
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchPayments = async (page = 1) => {
    setLoading(true);
    try {
      const body = {
        page,
        limit: meta.limit || 20,
      };
      if (filters.status) body.status = filters.status;
      if (filters.provider) body.provider = filters.provider;
      if (filters.organizationId.trim()) body.organizationId = filters.organizationId.trim();

      const response = await api.post('/admin/payments/list', body);
      setItems(response.data?.data || []);
      setMeta(response.data?.meta || { page, limit: 20, total: 0, totalPages: 1 });
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error(error.response?.data?.message || 'Failed to load payments');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (payment) => {
    setLoadingDetail(true);
    setDetail(null);
    try {
      const response = await api.post('/admin/payments/get', { id: payment.id });
      setDetail(response.data?.data?.payment || null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load payment detail');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchPayments(1);
  };

  if (loading && items.length === 0) {
    return <LoadingScreen message="Loading payments..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="Payments"
        description="Platform-wide payment history across organizations (gateway, status, amount)."
      />

      <form className="card card__body" onSubmit={handleFilterSubmit} style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="status">Status</label>
            <select
              id="status"
              className="form-select"
              value={filters.status}
              onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="authorized">Authorized</option>
              <option value="captured">Captured</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="provider">Gateway</label>
            <select
              id="provider"
              className="form-select"
              value={filters.provider}
              onChange={(e) => setFilters((p) => ({ ...p, provider: e.target.value }))}
            >
              <option value="">All</option>
              <option value="razorpay">Razorpay</option>
              <option value="idfc">IDFC</option>
              <option value="stripe">Stripe</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="organizationId">Organization ID</label>
            <input
              id="organizationId"
              className="form-input"
              value={filters.organizationId}
              onChange={(e) => setFilters((p) => ({ ...p, organizationId: e.target.value }))}
              placeholder="Optional"
            />
          </div>
          <div className="form-group" style={{ margin: 0, display: 'flex', alignItems: 'flex-end' }}>
            <button type="submit" className="btn btn--primary" disabled={loading}>
              {loading ? 'Loading...' : 'Apply'}
            </button>
          </div>
        </div>
      </form>

      {items.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No payments found"
            description="Payments will appear here after tenants complete checkout."
          />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table className="data-table data-table--responsive">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Organization</th>
                  <th>Amount</th>
                  <th>Gateway</th>
                  <th>Status</th>
                  <th>Reference</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((payment) => (
                  <tr key={payment.id}>
                    <td data-label="Date">
                      {payment.createdAt ? new Date(payment.createdAt).toLocaleString() : '—'}
                    </td>
                    <td data-label="Organization" className="cell-mono">
                      {payment.organizationId || '—'}
                    </td>
                    <td data-label="Amount">
                      {formatAmount(payment.amount, payment.currency)}
                      {payment.amountRefunded > 0 && <div className="form-hint">{formatInr(payment.amountRefunded)} refunded</div>}
                      {payment.providerMode && payment.providerMode !== 'live' && <span className="badge badge--info">Test</span>}
                    </td>
                    <td data-label="Gateway">{payment.provider || '—'}</td>
                    <td data-label="Status">
                      <StatusBadge status={payment.status || 'unknown'} />
                    </td>
                    <td data-label="Reference" className="cell-mono">
                      {payment.referenceNumber || payment.gatewayOrderId || '—'}
                    </td>
                    <td data-label="Actions" className="cell-actions">
                      <button
                        type="button"
                        className="btn btn--secondary btn--sm"
                        onClick={() => openDetail(payment)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {meta.totalPages > 1 && (
            <div className="btn-group" style={{ padding: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={meta.page <= 1 || loading}
                onClick={() => fetchPayments(meta.page - 1)}
              >
                Previous
              </button>
              <span className="form-hint">
                Page {meta.page} of {meta.totalPages} ({meta.total} total)
              </span>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={meta.page >= meta.totalPages || loading}
                onClick={() => fetchPayments(meta.page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {(detail || loadingDetail) && (
        <Modal
          title="Payment detail"
          subtitle={detail?.referenceNumber || detail?.id}
          onClose={() => setDetail(null)}
          size="lg"
        >
          {loadingDetail || !detail ? (
            <p>Loading...</p>
          ) : (
            <div>
              <div className="stats-grid" style={{ marginBottom: '1rem' }}>
                <div>
                  <div className="form-hint">Amount</div>
                  <strong>{formatAmount(detail.amount, detail.currency)}</strong>
                </div>
                <div>
                  <div className="form-hint">Status</div>
                  <StatusBadge status={detail.status} />
                </div>
                <div>
                  <div className="form-hint">Gateway</div>
                  <strong>{detail.provider}</strong>
                </div>
                <div>
                  <div className="form-hint">Method</div>
                  <strong>{detail.paymentMethod || '—'}</strong>
                </div>
              </div>
              <p className="form-hint cell-mono">Order: {detail.gatewayOrderId || '—'}</p>
              <p className="form-hint cell-mono">Payment: {detail.gatewayPaymentId || '—'}</p>
              <p className="form-hint cell-mono">Org: {detail.organizationId}</p>
              {detail.failureReason && (
                <p className="form-hint" style={{ color: 'var(--danger, #b91c1c)' }}>
                  Failure: {detail.failureReason}
                </p>
              )}
              {detail.invoiceId && (
                <p className="form-hint">
                  Invoice {detail.invoiceNumber || ''}{' '}
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => openInvoicePdf(detail.invoiceId).catch((err) => toast.error(platformErrorMessage(err, 'Could not open the PDF')))}>
                    View PDF
                  </button>
                </p>
              )}
              {detail.dispute && (
                <div className="alert alert--error">
                  Dispute {detail.dispute.status} for {formatInr(detail.dispute.amount)}{detail.dispute.reasonCode ? ` (${detail.dispute.reasonCode})` : ''}.
                </div>
              )}
              {Array.isArray(detail.refunds) && detail.refunds.length > 0 && (
                <>
                  <h3 style={{ marginTop: '1.25rem' }}>Refunds</h3>
                  <ul>
                    {detail.refunds.map((refund) => (
                      <li key={refund.id} className="form-hint">
                        {refund.createdAt ? new Date(refund.createdAt).toLocaleString() : ''} — {formatInr(refund.amount)} ({refund.status || '—'}){refund.reason ? ` — ${refund.reason}` : ''}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {canRefund(detail) && (
                <RefundForm
                  payment={detail}
                  onDone={() => {
                    openDetail(detail);
                    fetchPayments(meta.page);
                  }}
                />
              )}
              {Array.isArray(detail.transactions) && detail.transactions.length > 0 && (
                <>
                  <h3 style={{ marginTop: '1.25rem' }}>Transactions</h3>
                  <ul>
                    {detail.transactions.map((tx) => (
                      <li key={tx.id} className="form-hint">
                        {tx.createdAt ? new Date(tx.createdAt).toLocaleString() : ''} — {tx.event} ({tx.status || '—'})
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

export default Payments;
